import { useCallback, useEffect, useRef, useState } from "react";
import { getDatabaseSearch, normalizeDatabaseSearchSections } from "../services/database.service";
import { searchConfig } from "config";

export default function useMainEbsco(enabled, query, timePeriod) {
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);
  const page = useRef(1);
  const fetching = useRef(false);
  const hasMore = useRef(false);
  const count = searchConfig.databases.count;

  useEffect(() => {
    const version = ++requestVersion.current;
    setSection(null);
    page.current = 1;
    fetching.current = false;
    hasMore.current = false;
    setLoading(Boolean(enabled && query));
    if (!enabled || !query) return;
    getDatabaseSearch(query, timePeriod, ["Ebsco"], 1, count)
      .then((data) => {
        if (version !== requestVersion.current) return;
        const next = normalizeDatabaseSearchSections(data, ["Ebsco"])[0] || null;
        setSection(next);
        hasMore.current = count < (next?.webPages?.totalEstimatedMatches || 0);
      })
      .catch((error) => console.error("Unable to load EBSCO results", error))
      .finally(() => {
        if (version === requestVersion.current) setLoading(false);
      });
    return () => { requestVersion.current += 1; };
  }, [enabled, query, timePeriod, count]);

  const fetchMore = useCallback(async () => {
    if (!enabled || fetching.current || !hasMore.current) return;
    const version = requestVersion.current;
    fetching.current = true;
    try {
      const nextPage = page.current + 1;
      const data = await getDatabaseSearch(query, timePeriod, ["Ebsco"], nextPage, count);
      if (version !== requestVersion.current) return;
      const next = normalizeDatabaseSearchSections(data, ["Ebsco"])[0];
      const items = next?.webPages?.value || [];
      page.current = nextPage;
      hasMore.current = items.length > 0 && nextPage * count < (next?.webPages?.totalEstimatedMatches || 0);
      setSection((previous) => {
        const existing = previous?.webPages?.value || [];
        const urls = new Set(existing.map((item) => item.url));
        return { ...next, webPages: { ...next?.webPages, value: [...existing, ...items.filter((item) => !urls.has(item.url))] } };
      });
    } catch (error) {
      console.error("Unable to load more EBSCO results", error);
    } finally {
      if (version === requestVersion.current) fetching.current = false;
    }
  }, [enabled, query, timePeriod, count]);

  const canFetchMore = (section?.webPages?.value?.length || 0) < (section?.webPages?.totalEstimatedMatches || 0);
  return { section, loading, fetchMore: canFetchMore ? fetchMore : undefined };
}
